import React, {useEffect} from 'react';
import {Button, Modal, OverlayTrigger, Pagination, Tooltip} from '@themesberg/react-bootstrap';
import Utils from '../services/Utils';
import {useHistory, useLocation} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {FormattedMessage} from 'react-intl';
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faCheck,
  faCircleXmark,
  faEye,
  faLock,
  faPlus,
  faShare,
  faSort,
  faSortDown,
  faSortUp,
  faTrashAlt
} from '@fortawesome/free-solid-svg-icons';
import API from '../services/API';
import BookingModal from '../components/BookingModal';
import BookingConfirmModal from '../components/BookingConfirmModal';
import BookingShareModal from '../components/BookingShareModal';
import BookingCancelModal from '../components/BookingCancelModal';
import BookingCreateModal from '../components/BookingCreateModal';
import {faGoogle} from '@fortawesome/free-brands-svg-icons';
import GoogleModal from '../components/GoogleModal';
import GoogleErrorModal from '../components/GoogleErrorModal';
import ExportDropdown from '../components/ExportDropdown';
import {useNotifications} from '../services/NotificationsContext';
import {usePlan} from '../services/PlanContext';

// Bascule tableau (desktop) / cartes (mobile), comme CrudTable — un seul arbre monté à la fois.
const MOBILE_QUERY = '(max-width: 767.98px)';
const useIsMobile = () => {
  const get = () => (typeof window !== 'undefined' && window.matchMedia)
    ? window.matchMedia(MOBILE_QUERY).matches : false;
  const [isMobile, setIsMobile] = React.useState(get);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = () => setIsMobile(mql.matches);
    handler();
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);
  return isMobile;
};

const HOVER = ['hover'];
const intentClass = (intent) =>
  ({success: 'is-success', info: 'is-info', warn: 'is-warn', danger: 'is-danger'}[intent] || '');

// Colonnes triables (mêmes clés que le backend getAdminBookings) — pour le sélecteur de tri mobile,
// qui remplace les en-têtes cliquables du tableau desktop. `id` n'a pas de libellé i18n → « # ».
const SORT_COLUMNS = [
  {column: 'createdAt', labelId: 'ORDER_DATE'},
  {column: 'pickupAt', labelId: 'PICKUP_DATETIME'},
  {column: 'customer', labelId: 'CUSTOMER'},
  {column: 'vehicleName', labelId: 'VEHICLE'},
  {column: 'price', labelId: 'AMOUNT'},
  {column: 'status', labelId: 'STATUS'},
  {column: 'payment', labelId: 'PAYMENT'},
  {column: 'id', label: '#'},
];

export default (props) => {
  Utils.setBackground(false);

  const {intl} = props;
  const history = useHistory();
  const location = useLocation();
  const {markOrdersSeen} = useNotifications();
  const {openSubscription} = usePlan();
  const [bookings, setBookings] = React.useState([]);
  const [activeItem, setActiveItem] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [sortBy, setSortBy] = React.useState('createdAt');
  const [sortOrder, setSortOrder] = React.useState('desc');
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [showCancelModal, setShowCancelModal] = React.useState(false);
  const [showDetailsModal, setShowDetailsModal] = React.useState(false);
  const [showShareModal, setShowShareModal] = React.useState(false);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [showGoogleModal, setShowGoogleModal] = React.useState(false);
  const [showGoogleErrorModal, setShowGoogleErrorModal] = React.useState(false);
  const [currentBookingId, setCurrentBookingId] = React.useState(0);
  const [lengthUnit, setLengthUnit] = React.useState('KM');
  const isMobile = useIsMobile();

  const handleConfirmBookingButton = (id) => {
    setCurrentBookingId(id);
    setShowConfirmModal(true);
  }

  const handleCancelBookingButton = (id) => {
    setCurrentBookingId(id);
    setShowCancelModal(true);
  }

  const handleDeleteBookingButton = (id) => {
    setCurrentBookingId(id);
    setShowDeleteModal(true);
  }

  const handleDetailsBookingButton = (id) => {
    setCurrentBookingId(id);
    setShowDetailsModal(true);
  }

  const handleShareBookingButton = (id) => {
    setCurrentBookingId(id);
    setShowShareModal(true);
  }

  const handleDeleteBooking = () => {
    API.deleteAdminBooking(history, currentBookingId).then(response => {
      if (response && response.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'BOOKING_DELETED'}));
        setShowDeleteModal(false);
        refreshBookings(activeItem);
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  // Statut + paiement (variante de couleur + libellé traduit) — partagé tableau/carte.
  const statusInfo = (b) => ({
    statusVariant: b.status === 'CONFIRMED' ? 'success' : b.status === 'VALIDATED' ? 'warning' : b.status === 'EXPIRED' ? 'danger' : '',
    paymentVariant: b.payment === 'PAID' ? 'success' : (b.payment === 'FAILED' || b.payment === 'ABANDONED') ? 'danger' : '',
    statusText: b.status === 'CONFIRMED' ? intl.formatMessage({id: 'CONFIRMED'})
      : b.status === 'CANCELED' ? intl.formatMessage({id: 'CANCELED'})
        : b.status === 'VALIDATED' ? intl.formatMessage({id: 'WAITING_CONFIRMATION'})
          : b.status === 'EXPIRED' ? intl.formatMessage({id: 'EXPIRED'}) : '',
    paymentText: b.payment === 'PAID' ? intl.formatMessage({id: 'PAID'})
      : b.payment === 'FAILED' ? intl.formatMessage({id: 'PAYMENT_FAILED'})
        : b.payment === 'PENDING' ? intl.formatMessage({id: 'WAITING_FOR_PAYMENT'})
          : b.payment === 'ABANDONED' ? intl.formatMessage({id: 'PAYMENT_NOT_FINALIZED'}) : '',
  });

  const formatPrice = (b) => new Intl.NumberFormat(navigator.language, {style: 'currency', currency: b.currency})
    .format(b.discount ? b.discount.discountPrice : b.price);

  // Liste d'actions par réservation — source unique pour le tableau ET les cartes.
  // Réservation masquée (gratuit) : ni confirmation ni partage (divulgueraient la course) ;
  // annulation/suppression restent permises. Le backend refuse aussi (BOOKING_LOCKED).
  const actionsFor = (b) => [
    {key: 'details', icon: faEye, labelId: 'VIEW_DETAILS', intent: 'neutral', onClick: () => handleDetailsBookingButton(b.id)},
    {key: 'confirm', icon: faCheck, labelId: 'CONFIRM', intent: 'success', onClick: () => handleConfirmBookingButton(b.id), hidden: b.status !== 'VALIDATED' || b.blurred},
    {key: 'cancel', icon: faCircleXmark, labelId: 'CANCEL', intent: 'warn', onClick: () => handleCancelBookingButton(b.id), hidden: b.status !== 'VALIDATED'},
    {key: 'share', icon: faShare, labelId: 'SHARE', intent: 'info', onClick: () => handleShareBookingButton(b.id), hidden: b.blurred},
    {key: 'delete', icon: faTrashAlt, labelId: 'DELETE', intent: 'danger', onClick: () => handleDeleteBookingButton(b.id)},
  ].filter(a => !a.hidden);

  const lockedName = (b) => (
    <button type="button" className="ccv2-booking-locked"
            title={intl.formatMessage({id: 'BLURRED_BOOKING_HINT'})}
            onClick={(e) => { e.stopPropagation(); openSubscription(); }}>
      <FontAwesomeIcon icon={faLock}/> <FormattedMessage id="BLURRED_BOOKING"/>
    </button>
  );

  // Actions en icônes (desktop) — tooltip au survol, couleur par intention. On retire « Voir » :
  // la ligne entière est déjà cliquable pour ouvrir le détail (les cartes mobiles le gardent).
  const desktopActions = (b) => (
    <div className="ccv2-crud-actions">
      {actionsFor(b).filter(a => a.key !== 'details').map(a => (
        <OverlayTrigger key={a.key} trigger={HOVER} overlay={<Tooltip>{intl.formatMessage({id: a.labelId})}</Tooltip>}>
          <button type="button" className={`ccv2-crud-iconbtn ${intentClass(a.intent)}`}
                  aria-label={intl.formatMessage({id: a.labelId})}
                  onClick={(e) => { e.stopPropagation(); a.onClick(); }}>
            <FontAwesomeIcon icon={a.icon}/>
          </button>
        </OverlayTrigger>
      ))}
    </div>
  );

  // Actions larges (icône + libellé) pour les cartes mobiles.
  const mobileActions = (b) => (
    <div className="ccv2-crud-mobile-actions">
      {actionsFor(b).map(a => (
        <button key={a.key} type="button" className={`ccv2-crud-mbtn ${intentClass(a.intent)}`} onClick={() => a.onClick()}>
          <FontAwesomeIcon icon={a.icon}/> <span>{intl.formatMessage({id: a.labelId})}</span>
        </button>
      ))}
    </div>
  );

  const TableRow = ({b}) => {
    const {statusVariant, statusText, paymentVariant, paymentText} = statusInfo(b);
    return (
      <tr
        onClick={() => handleDetailsBookingButton(b.id)}
        style={{cursor: 'pointer'}}
        className={`ccv2-booking-row ${b.blurred ? 'ccv2-booking-blurred' : ''}`}
      >
        <td className="td-center">{Utils.formatBookingNumber({id: b.id, bookingNumber: b.bookingNumber})}</td>
        <td className="fw-bold td-center">
          {b.blurred ? lockedName(b) : <>{b.customer?.firstName ?? ''} {b.customer?.lastName ?? ''}</>}
        </td>
        <td className="td-center">{b.vehicle}</td>
        <td className="td-center">{b.pickUpTime}{b.returnPickUpTime && (<span><br/>{b.returnPickUpTime}</span>)}</td>
        <td className="td-center">{formatPrice(b)}</td>
        <td className="td-center"><span className={`text-${statusVariant}`}>{statusText}</span></td>
        <td className="td-center">
          {b.paymentMode === 'ONLINE' && <span className={`text-${paymentVariant}`}>{paymentText}</span>}
          {b.paymentMode === 'DRIVER' && <FormattedMessage id="DRIVER_PAYMENT"/>}
        </td>
        <td className="td-center">{b.orderDate}</td>
        <td className="td-center" onClick={(e) => e.stopPropagation()}>{desktopActions(b)}</td>
      </tr>
    );
  };

  // Carte mobile — même contenu que la ligne, empilé (zéro scroll horizontal).
  const MobileCard = ({b}) => {
    const {statusVariant, statusText, paymentVariant, paymentText} = statusInfo(b);
    return (
      <div className={`ccv2-crud-mobile-card ${b.blurred ? 'ccv2-booking-blurred' : ''}`}>
        <div className="ccv2-crud-mc-head">
          <div className="ccv2-crud-mc-title">
            {b.blurred ? lockedName(b) : <>{b.customer?.firstName ?? ''} {b.customer?.lastName ?? ''}</>}
          </div>
          {statusText && <span className={`text-${statusVariant} fw-bold`}>{statusText}</span>}
        </div>
        <div className="ccv2-crud-mv-row">
          <span className="ccv2-crud-mv-label">#</span>
          <span className="ccv2-crud-mv-value">{Utils.formatBookingNumber({id: b.id, bookingNumber: b.bookingNumber})}</span>
        </div>
        <div className="ccv2-crud-mv-row">
          <span className="ccv2-crud-mv-label"><FormattedMessage id="VEHICLE"/></span>
          <span className="ccv2-crud-mv-value">{b.vehicle}</span>
        </div>
        <div className="ccv2-crud-mv-row">
          <span className="ccv2-crud-mv-label"><FormattedMessage id="PICKUP_DATETIME"/></span>
          <span className="ccv2-crud-mv-value">{b.pickUpTime}{b.returnPickUpTime && (<><br/>{b.returnPickUpTime}</>)}</span>
        </div>
        <div className="ccv2-crud-mv-row">
          <span className="ccv2-crud-mv-label"><FormattedMessage id="AMOUNT"/></span>
          <span className="ccv2-crud-mv-value is-numeric">{formatPrice(b)}</span>
        </div>
        <div className="ccv2-crud-mv-row">
          <span className="ccv2-crud-mv-label"><FormattedMessage id="PAYMENT"/></span>
          <span className="ccv2-crud-mv-value">
            {b.paymentMode === 'ONLINE' && <span className={`text-${paymentVariant}`}>{paymentText}</span>}
            {b.paymentMode === 'DRIVER' && <FormattedMessage id="DRIVER_PAYMENT"/>}
          </span>
        </div>
        <div className="ccv2-crud-mv-row">
          <span className="ccv2-crud-mv-label"><FormattedMessage id="ORDER_DATE"/></span>
          <span className="ccv2-crud-mv-value">{b.orderDate}</span>
        </div>
        {mobileActions(b)}
      </div>
    );
  };

  const onPrevItem = () => {
    const prevActiveItem = activeItem === 1 ? activeItem : activeItem - 1;
    if (activeItem !== prevActiveItem) {
      setActiveItem(prevActiveItem);
      refreshBookings(prevActiveItem);
    }
  };

  const onNextItem = (totalPages) => {
    const nextActiveItem = activeItem === totalPages ? activeItem : activeItem + 1;
    if (activeItem !== nextActiveItem) {
      setActiveItem(nextActiveItem);
      refreshBookings(nextActiveItem);
    }
  };

  const items = [];
  let previousDots = false;
  let nextDots = false;
  for (let number = 1; number <= totalPages; number++) {
    const isItemActive = activeItem === number;

    const handlePaginationChange = () => {
      setActiveItem(number);
      refreshBookings(number);
    };

    if (totalPages <= 5) {
      items.push(
        <Pagination.Item active={isItemActive} key={number} onClick={handlePaginationChange}>
          {number}
        </Pagination.Item>
      );
    } else {
      if (isItemActive || ((activeItem + 3) >= number && number > activeItem - 3) || ((activeItem - 3) <= number && number < activeItem + 3)) {
        items.push(
          <Pagination.Item active={isItemActive} key={number} onClick={handlePaginationChange}>
            {number}
          </Pagination.Item>
        );
      } else if (!previousDots && number < activeItem) {
        previousDots = true;
        items.push(
          <Pagination.Item active={isItemActive} key="previousDots">
            ...
          </Pagination.Item>
        );
      } else if (!nextDots && number > activeItem) {
        nextDots = true;
        items.push(
          <Pagination.Item active={isItemActive} key="nextDots">
            ...
          </Pagination.Item>
        );
      }
    }
  }

  const refreshBookings = (page, sortByOverride, sortOrderOverride) => {
    const effectiveSortBy = sortByOverride !== undefined ? sortByOverride : sortBy;
    const effectiveSortOrder = sortOrderOverride !== undefined ? sortOrderOverride : sortOrder;
    API.getAdminBookings(history, page, effectiveSortBy, effectiveSortOrder).then(response => {
      if (response && response.data.success === true) {
        setBookings(response.data.data.bookings);
        setTotalPages(response.data.data.pagination.totalPages)
        setLengthUnit(response.data.data.lengthUnit === 'MILE' ? 'MI' : 'KM');
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  const handleSort = (column) => {
    let nextOrder;
    if (sortBy === column) {
      nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // Date columns feel more natural starting on most-recent-first; everything
      // else (text, numbers, status) starts ascending.
      nextOrder = (column === 'pickupAt' || column === 'createdAt') ? 'desc' : 'asc';
    }
    setSortBy(column);
    setSortOrder(nextOrder);
    setActiveItem(1);
    refreshBookings(1, column, nextOrder);
  };

  // Sélecteur de tri mobile : inverse le sens sur la colonne courante.
  const toggleSortOrder = () => {
    const next = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(next);
    setActiveItem(1);
    refreshBookings(1, sortBy, next);
  };

  const SortableHeader = ({column, labelId, label}) => {
    const isActive = sortBy === column;
    const icon = !isActive ? faSort : (sortOrder === 'asc' ? faSortUp : faSortDown);
    return (
      <th
        className={`ccv2-sortable ${isActive ? 'is-active' : ''}`}
        onClick={() => handleSort(column)}
        title={intl.formatMessage({id: 'SORT_COLUMN'})}
      >
        {labelId ? <FormattedMessage id={labelId}/> : label}
        <FontAwesomeIcon icon={icon} className="ccv2-sort-icon"/>
      </th>
    );
  };

  useEffect(() => {
    if (!Utils.isAccessToken(history)) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (params.get('error') === 'google') {
      setShowGoogleErrorModal(true);
    }

    // Visiting Orders clears the sidebar "new bookings" badge.
    markOrdersSeen();

    refreshBookings(1);
  }, []);


  return (
    <div className="ccv2-crud">
      <div className="ccv2-toolbar ccv2-toolbar-spread">
        <div className="ccv2-toolbar-group">
          <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={() => setShowCreateModal(true)}>
            <FontAwesomeIcon icon={faPlus}/> <FormattedMessage id="NEW_BOOKING"/>
          </button>
          <button type="button" className="ccv2-btn ccv2-btn-google" onClick={() => setShowGoogleModal(true)}>
            <FontAwesomeIcon icon={faGoogle}/> <FormattedMessage id="GOOGLE_CALENDAR"/>
          </button>
        </div>
        <ExportDropdown intl={intl} filename={intl.formatMessage({id: 'BOOKINGS'})}
                        request={(format) => API.getBookingsExport(history, format)}/>
      </div>

      {isMobile ? (
        // Mobile : cartes empilées (plus de tableau à faire défiler horizontalement),
        // précédées d'un sélecteur de tri qui pilote les mêmes sortBy/sortOrder que le backend.
        <>
          <div className="ccv2-crud-mobile-sort">
            <span className="ccv2-crud-sort-label"><FormattedMessage id="SORT_BY"/></span>
            <select value={sortBy} onChange={(e) => handleSort(e.target.value)}>
              {SORT_COLUMNS.map(c => (
                <option key={c.column} value={c.column}>
                  {c.labelId ? intl.formatMessage({id: c.labelId}) : c.label}
                </option>
              ))}
            </select>
            <button type="button" className="ccv2-crud-sort-dir" onClick={toggleSortOrder}
                    title={intl.formatMessage({id: 'SORT_DIRECTION'})}
                    aria-label={intl.formatMessage({id: 'SORT_DIRECTION'})}>
              <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortUp : faSortDown}/>
            </button>
          </div>
          <div className="ccv2-crud-mobile-list">
            {bookings && bookings.map(b => <MobileCard key={`booking-${b.id}`} b={b}/>)}
          </div>
        </>
      ) : (
        <div className="ccv2-crud-card" style={{minHeight: '420px'}}>
          <div className="ccv2-crud-scroll">
            <table className="ccv2-crud-table">
              <thead>
              <tr>
                <SortableHeader column="id" label="#"/>
                <SortableHeader column="customer" labelId="CUSTOMER"/>
                <SortableHeader column="vehicleName" labelId="VEHICLE"/>
                <SortableHeader column="pickupAt" labelId="PICKUP_DATETIME"/>
                <SortableHeader column="price" labelId="AMOUNT"/>
                <SortableHeader column="status" labelId="STATUS"/>
                <SortableHeader column="payment" labelId="PAYMENT"/>
                <SortableHeader column="createdAt" labelId="ORDER_DATE"/>
                <th><FormattedMessage id="ACTIONS"/></th>
              </tr>
              </thead>
              <tbody>
              {bookings && bookings.map(b => <TableRow key={`booking-${b.id}`} b={b}/>)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="ccv2-crud-pagination">
        <Pagination size="md" className="mt-3">
          <Pagination.Prev onClick={onPrevItem}>
            <FontAwesomeIcon icon={faAngleDoubleLeft}/>
          </Pagination.Prev>
          {items}
          <Pagination.Next onClick={() => onNextItem(totalPages)}>
            <FontAwesomeIcon icon={faAngleDoubleRight}/>
          </Pagination.Next>
        </Pagination>
      </div>
        <Modal as={Modal.Dialog} centered show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header>
            <Modal.Title className="h6"><FormattedMessage id="DELETE_BOOKING"/></Modal.Title>
            <Button variant="close" aria-label="Close" onClick={() => setShowDeleteModal(false)}/>
          </Modal.Header>
          <Modal.Body>
            <FormattedMessage id="NO_LONGER_SEE_THIS_BOOKING"/>
            <br/>
            <FormattedMessage id="CUSTOMER_NOT_NOTIFIED_BY_EMAIL"/>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="danger" onClick={handleDeleteBooking}>
              <FormattedMessage id="DELETE"/>
            </Button>
            <Button variant="link" className="text-gray ms-auto" onClick={() => setShowDeleteModal(false)}>
              <FormattedMessage id="CLOSE"/>
            </Button>
          </Modal.Footer>
        </Modal>
        {currentBookingId != null &&
          <BookingModal intl={intl}
                        lengthUnit={lengthUnit}
                        booking={bookings.find(b => b.id === currentBookingId)}
                        show={showDetailsModal}
                        onClose={() => setShowDetailsModal(false)}
                        onRefresh={() => refreshBookings(activeItem)}>
          </BookingModal>
        }
        <BookingConfirmModal intl={intl} bookingId={currentBookingId} show={showConfirmModal} onClose={() => setShowConfirmModal(false)} onRefresh={() => refreshBookings(activeItem)}></BookingConfirmModal>
        <BookingCancelModal intl={intl} bookingId={currentBookingId} show={showCancelModal} onClose={() => setShowCancelModal(false)} onRefresh={() => refreshBookings(activeItem)}></BookingCancelModal>
        <BookingShareModal intl={intl} bookingId={currentBookingId} show={showShareModal} onClose={() => setShowShareModal(false)}></BookingShareModal>
        <BookingCreateModal intl={intl} lengthUnit={lengthUnit} show={showCreateModal} onClose={() => setShowCreateModal(false)} onRefresh={() => refreshBookings(activeItem)}></BookingCreateModal>
        <GoogleModal history={history} intl={intl} show={showGoogleModal} onClose={() => setShowGoogleModal(false)}></GoogleModal>
        <GoogleErrorModal show={showGoogleErrorModal} onClose={() => setShowGoogleErrorModal(false)}></GoogleErrorModal>
    </div>
  );
};
